import React from 'react';
import { useRouter } from 'next/compat/router';

import useUI from 'hooks/use-ui';
import { useModals, ModalNames } from 'src/components/modals';
import { GoogleLogin, AppleLogin } from 'src/components/social-login';
import BlueSparkle from 'src/assets/blue-sparkle-icon';

import { tracker } from 'src/utils/analytics';
import { getItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/helpers/item-list-name';
import {
  ProductRecommendationSection,
  ProductRecommendationSource,
} from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

import {
  Content,
  Subheading,
  Wrapper,
  Heading,
  AlreadyHaveAccount,
  LoginButton,
  ButtonWrapper,
  SignUpContainer,
  ButtonsContainer,
  HeadingsContainer,
  TextContent,
  SocialLoginWrapper,
  IconContainer,
} from './logged-out-v2.styles';

type TrackUserEventProps = {
  signIn: boolean;
  type: 'email' | 'sso';
};

export function LoggedOutV2(): JSX.Element | null {
  const { isKiosk } = useUI();
  const router = useRouter();
  const { openModal } = useModals();

  if (isKiosk) {
    return null;
  }

  const trackerSource = router
    ? getItemListName({
        router,
        source: ProductRecommendationSource.native,
        section: ProductRecommendationSection.guestRecommendations,
      })
    : null;

  const trackUserEvent = ({ signIn, type }: TrackUserEventProps): void => {
    tracker.gaGTMClickEvent({
      eventCategory: 'account',
      eventAction: signIn ? 'signed in' : 'created account',
      eventLabel: `${type}: {pageType}`,
    });
  };

  const handleOpenLoginModal = (): void => {
    void openModal(ModalNames.login, trackerSource);
    trackUserEvent({ signIn: true, type: 'email' });
  };

  const handleSocialLoginSubmit = (): void => {
    trackUserEvent({ signIn: true, type: 'sso' });
  };

  return (
    <Wrapper data-testid='personalized-products-carousel-logged-out-v2-cta'>
      <Content>
        <HeadingsContainer>
          <IconContainer>
            <BlueSparkle />
          </IconContainer>
          <TextContent>
            <Heading size='medium' weight='bold' tag='h2'>
              Log in for the best experience
            </Heading>
            <Subheading size='medium' weight='regular' tag='p'>
              Enjoy personalized recommendations, faster checkout, and quick reordering of your favorites.
            </Subheading>
          </TextContent>
        </HeadingsContainer>
        <SignUpContainer>
          <ButtonsContainer>
            <ButtonWrapper>
              <SocialLoginWrapper data-testid='google-login-wrapper'>
                <GoogleLogin redirectUrl={window.location.href} onSubmit={handleSocialLoginSubmit} />
              </SocialLoginWrapper>

              <SocialLoginWrapper>
                <AppleLogin redirectUrl={window.location.href} onSubmit={handleSocialLoginSubmit} />
              </SocialLoginWrapper>
            </ButtonWrapper>
          </ButtonsContainer>

          <AlreadyHaveAccount size='medium'>
            <LoginButton onClick={handleOpenLoginModal}>Log in or sign up with email</LoginButton>
          </AlreadyHaveAccount>
        </SignUpContainer>
      </Content>
    </Wrapper>
  );
}
